import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Shield, FileText, Scale } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-10 h-10 text-red-400" />
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                  Terms of Service
                </h1>
                <p className="text-muted-foreground">
                  Last updated: February 2026
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                1. Agreement to Terms
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  Welcome to SportsAnalysisAB ("Company", "we", "us", or "our"), an Alberta-based sports analytics platform. 
                  By accessing or using our website at sportsanalysisab.ca (the "Service"), you agree to be bound by these 
                  Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
                </p>
                <p>
                  These Terms constitute a legally binding agreement between you and SportsAnalysisAB, 
                  operated under the laws of the Province of Alberta, Canada.
                </p>
              </div>
            </section>

            {/* Age Requirement */}
            <section className="bg-red-500/10 border border-red-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-red-400 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                2. Age Restriction - 18+ Only
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  <strong className="text-foreground">You must be at least 18 years of age to use this Service.</strong> 
                  By accessing SportsAnalysisAB, you represent and warrant that you are at least 18 years old 
                  and have the legal capacity to enter into this agreement.
                </p>
                <p>
                  In accordance with Alberta's <em>Gaming, Liquor and Cannabis Act</em> and related regulations, 
                  we require all users to confirm their age before accessing our analytics content. 
                  We reserve the right to request proof of age and to terminate accounts of users 
                  who do not meet the age requirement.
                </p>
              </div>
            </section>

            {/* Nature of Service */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                3. Nature of Service - Entertainment Only
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  SportsAnalysisAB provides statistical game previews, probability estimates, and sports analytics 
                  <strong className="text-foreground"> FOR ENTERTAINMENT AND EDUCATIONAL PURPOSES ONLY</strong>.
                </p>
                <p>
                  <strong className="text-foreground">Important Disclaimers:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We are NOT a gambling or betting service</li>
                  <li>We do NOT accept, facilitate, or process any wagers or bets</li>
                  <li>Our predictions and analyses are NOT betting advice</li>
                  <li>Past performance does NOT guarantee future results</li>
                  <li>You should NOT make financial decisions based solely on our content</li>
                  <li>We are NOT affiliated with any professional sports leagues or gambling organizations</li>
                </ul>
                <p>
                  Our probability estimates and predictions are generated using statistical models and historical data. 
                  They represent mathematical probabilities and should be treated as informational content only.
                </p>
              </div>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                4. User Accounts and Registration
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>To access certain features, you must create an account. You agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate, current, and complete registration information</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized access</li>
                </ul>
                <p>
                  We reserve the right to suspend or terminate accounts that violate these Terms 
                  or for any other reason at our sole discretion.
                </p>
              </div>
            </section>

            {/* Subscription Terms */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                5. Subscription and Payment Terms
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  Premium features are available through paid subscriptions. By subscribing, you agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Pay the subscription fees as displayed at the time of purchase</li>
                  <li>Automatic renewal unless cancelled before the renewal date</li>
                  <li>Provide accurate billing information</li>
                </ul>
                <p>
                  <strong className="text-foreground">Cancellation:</strong> You may cancel your subscription at any time through your account settings. 
                  Cancellation will take effect at the end of the current billing period. 
                  In accordance with Alberta's <em>Consumer Protection Act</em>, you may be entitled to a refund 
                  within certain cooling-off periods for digital purchases.
                </p>
                <p>
                  Payments are processed securely through Stripe. We do not store your complete credit card information.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                6. Intellectual Property
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  All content on SportsAnalysisAB, including but not limited to text, graphics, logos, 
                  algorithms, analyses, and software, is the property of SportsAnalysisAB or its licensors 
                  and is protected by Canadian copyright law and international treaties.
                </p>
                <p>
                  You may not reproduce, distribute, modify, or create derivative works without our express written permission.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-yellow-400 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5" />
                7. Limitation of Liability
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, INCLUDING THE <em>CONSUMER PROTECTION ACT</em> (ALBERTA):
                </p>
                <p>
                  SportsAnalysisAB and its directors, employees, partners, agents, and affiliates shall not be liable for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Any loss of profits, revenue, data, or goodwill</li>
                  <li>Any damages arising from your use of or reliance on our predictions or analyses</li>
                  <li>Any losses incurred through gambling or betting activities</li>
                </ul>
                <p>
                  Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim.
                </p>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                8. Governing Law and Jurisdiction
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the 
                  Province of Alberta and the federal laws of Canada applicable therein, 
                  without regard to conflict of law principles.
                </p>
                <p>
                  Any disputes arising from these Terms or your use of the Service shall be subject 
                  to the exclusive jurisdiction of the courts of the Province of Alberta.
                </p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                9. Changes to Terms
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  We reserve the right to modify these Terms at any time. We will notify users of 
                  material changes by posting the updated Terms on our website and updating the 
                  "Last updated" date. Your continued use of the Service after changes constitute 
                  acceptance of the modified Terms.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                10. Contact Information
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  For questions about these Terms of Service, please contact us at:
                </p>
                <p>
                  <strong className="text-foreground">SportsAnalysisAB</strong><br />
                  Email: Contact Us page<br />
                  Province of Alberta, Canada
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
